<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import type { Temporal } from '@shared/lib/date-time';

import { parsePlainDate } from '@shared/lib/date-time';

import { DatePicker } from '../date-picker';
import { AppInput } from '../input';
import { formatTypedDate, parseTypedDate, pickRangeDay } from './date-range';

const props = withDefaults(
  defineProps<{ today: Temporal.PlainDate | null; error: string | null; errorId: string; months?: 1 | 2 }>(),
  { months: 1 },
);
const dateFrom = defineModel<string>('dateFrom', { required: true });
const dateTo = defineModel<string>('dateTo', { required: true });

const fields = [
  { label: 'С даты', prefix: 'с', model: dateFrom, text: ref(formatTypedDate(dateFrom.value)) },
  { label: 'По дату', prefix: 'по', model: dateTo, text: ref(formatTypedDate(dateTo.value)) },
];

// Период могли изменить календарём или быстрой кнопкой; то, что человек ещё печатает, не трогаем.
fields.forEach(({ model, text }) => {
  watch(model, (value) => {
    if (parseTypedDate(text.value)?.toString() !== value) text.value = formatTypedDate(value);
  });
});

type Field = (typeof fields)[number];

const handleInput = (field: Field, value: string | number): void => {
  field.text.value = String(value);
  const date = parseTypedDate(field.text.value);

  if (date) field.model.value = date.toString();
  else if (!field.text.value.trim()) field.model.value = '';
};

// Ошибку показываем, только когда человек закончил ввод: иначе она мигала бы на каждой цифре.
const commit = (field: Field): void => {
  const date = parseTypedDate(field.text.value);
  const value = field.text.value.trim();

  field.model.value = date ? date.toString() : value;
  if (date) field.text.value = formatTypedDate(date.toString());
};

const range = computed(() => ({ from: parsePlainDate(dateFrom.value), to: parsePlainDate(dateTo.value) }));

const pickDay = (day: Temporal.PlainDate): void => {
  const next = pickRangeDay({ dateFrom: dateFrom.value, dateTo: dateTo.value }, day.toString());
  dateFrom.value = next.dateFrom;
  dateTo.value = next.dateTo;
};

// Месяц из сетки месяцев берём целиком; текущий — по сегодня, как быстрый период.
const pickMonth = (first: Temporal.PlainDate): void => {
  const { today } = props;
  const isCurrent = today !== null && today.year === first.year && today.month === first.month;
  dateFrom.value = first.toString();
  dateTo.value = (isCurrent ? today : first.with({ day: first.daysInMonth })).toString();
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <div
      class="grid gap-2"
      :class="{ 'grid-cols-2 gap-x-6': months === 2 && $slots.presets }"
    >
      <slot name="presets" />
      <div class="flex min-w-0 items-center gap-2 text-sm text-(--base-popup-secondary-color)">
        <template
          v-for="field in fields"
          :key="field.label"
        >
          <span aria-hidden="true">{{ field.prefix }}</span>
          <AppInput
            class="min-w-0 flex-1"
            :model-value="field.text.value"
            :aria-label="field.label"
            placeholder="дд.мм.гггг"
            inputmode="numeric"
            autocomplete="off"
            :aria-invalid="Boolean(error)"
            :aria-describedby="error ? errorId : undefined"
            @update:model-value="handleInput(field, $event)"
            @blur="commit(field)"
            @keydown.enter="commit(field)"
          />
        </template>
      </div>
    </div>
    <DatePicker
      v-if="today"
      :model-value="null"
      :today="today"
      :range="range"
      :months="months"
      embedded
      @update:model-value="pickDay"
      @month="pickMonth"
    />
  </div>
</template>
